import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {pageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {fetchWithTracing} from '../../helpers/fetch-with-tracing'
import {useQueries, useQueryClient} from '@github-ui/react-query'
import type {StackMergeabilityPageData} from '../payloads/stack-mergeability'
import type {MergeMethod} from '../../types'

function stackMergeabilityPageDataQueryKey(basePageDataUrl: string, currentPullRequestId: string) {
  return [
    PageData.stackMergeability,
    `basePageDataURL:${basePageDataUrl}`,
    `currentPullRequestId:${currentPullRequestId}`,
  ]
}

export function useStackMergeabilityQuery({
  basePageDataUrl,
  currentPullRequestId,
  enabled = true,
  mergeMethod,
  bypassRequirements = false,
}: {
  basePageDataUrl: string
  currentPullRequestId: string
  mergeMethod: MergeMethod
  enabled?: boolean
  bypassRequirements?: boolean
}) {
  return useStackMergeabilityQueries({
    pulls: [{enabled, basePageDataUrl}],
    currentPullRequestId,
    mergeMethod,
    bypassRequirements,
  })[0]!
}

export function useStackMergeabilityQueries({
  pulls,
  currentPullRequestId,
  mergeMethod,
  bypassRequirements = false,
}: {
  pulls: Array<{enabled: boolean; basePageDataUrl: string}>
  currentPullRequestId: string
  mergeMethod: MergeMethod
  bypassRequirements?: boolean
}) {
  const queryClient = useQueryClient()

  const searchParams = new URLSearchParams()
  searchParams.append('merge_method', mergeMethod)
  searchParams.append('bypass_requirements', bypassRequirements.toString())

  const queries = pulls.map(({enabled, basePageDataUrl}) => {
    const apiURL = pageDataUrl(basePageDataUrl, PageData.stackMergeability, searchParams.toString())
    const queryKey = stackMergeabilityPageDataQueryKey(basePageDataUrl, currentPullRequestId)
    return {
      queryKey,
      queryFn: async () => {
        return fetchWithTracing<StackMergeabilityPageData>(apiURL, queryKey, queryClient)
      },
      staleTime: Infinity,
      enabled,
    }
  })

  return useQueries({queries})
}
